import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Gender,
  UserFlagsDisplayFlag,
  type Maybe,
} from '@xing-com/crate-common-graphql-types';
import { Flag, type FlagVariant } from '@xing-com/flag';
import { LazyLoading } from '@xing-com/lazy-loading';
import { PopUpWindow } from '@xing-com/pop-up-window';
import { ProfileImage } from '@xing-com/profile-image';
import { BodyCopy, Meta } from '@xing-com/typography';

import {
  GetArticleAuthorFollowersByIdDocument,
  type GetArticleAuthorFollowersByIdQuery,
  type GetArticleAuthorFollowersByIdQueryVariables,
  type ArticleAuthorFollowersPageInfoFragment,
  type ArticleAuthorFollowerFragment,
  type ArticleAuthorFollowersWithinContactsFragment,
} from '../../../graphql/get-article-author-followers.gql-types';
import { getFollowersContactsKey } from '../../../helpers';
import { type ArticleAuthorType } from '../../../types';

import ArticleAuthorFollowersSkeleton from './article-author-followers-skeleton';
import { Styled } from './styles';

const FOLLOWER_CONTACTS__QUERY_LIMIT = 20;

const getFlagVariant = (displayFlag: UserFlagsDisplayFlag): FlagVariant => {
  switch (displayFlag) {
    case UserFlagsDisplayFlag.Premium:
      return 'premium';
    case UserFlagsDisplayFlag.Insider:
      return 'insider';
    default:
      return 'basic';
  }
};

type ArticleAuthorFollowerEntryProps = {
  follower: Maybe<ArticleAuthorFollowerFragment>;
};

const ArticleAuthorFollowerEntry: React.FC<ArticleAuthorFollowerEntryProps> = ({
  follower,
}) => {
  const profileImage = follower?.node.xingId?.profileImage?.[0]?.url || '';
  const displayName = follower?.node.xingId?.displayName || '';
  const pageName = follower?.node.xingId?.pageName || '';
  const gender = follower?.node.xingId?.gender || Gender.None;
  const userFlag =
    follower?.node.xingId?.userFlags?.displayFlag || UserFlagsDisplayFlag.Basic;
  const company = follower?.node.xingId?.occupations?.[0]?.headline || '';
  const jobRole = follower?.node.xingId?.occupations?.[0]?.subline || '';

  const profilePageURL = `/profile/${pageName}?sc_o=news_articledetailpage_follower_profile_click`;

  return (
    <Styled.ArticleAuthorFollowerEntryContainer
      data-qa={`article-viewer-author-infobox-follower-${pageName}`}
    >
      <Styled.ArticleAuthorFollowerLink href={profilePageURL}>
        {/* @ts-expect-error FIXME: SC6 */}
        <ProfileImage gender={gender} src={profileImage} />
      </Styled.ArticleAuthorFollowerLink>

      <Styled.ArticleAuthorFollowerEntryInfoContainer>
        <Styled.ArticleAuthorFollowerTitle size="large" noMargin>
          <span>
            <Styled.ArticleAuthorFollowerLink href={profilePageURL}>
              {displayName}
            </Styled.ArticleAuthorFollowerLink>
          </span>
          {userFlag ? (
            <Flag
              variant={getFlagVariant(userFlag)}
              size="small"
              sizeWide="medium"
            />
          ) : null}
        </Styled.ArticleAuthorFollowerTitle>
        <BodyCopy noMargin>{company}</BodyCopy>
        <Meta noMargin>{jobRole}</Meta>
      </Styled.ArticleAuthorFollowerEntryInfoContainer>
    </Styled.ArticleAuthorFollowerEntryContainer>
  );
};

type FollowersInfoType = {
  total: number;
  followers: Array<Maybe<ArticleAuthorFollowerFragment>>;
  pageInfo: Maybe<ArticleAuthorFollowersPageInfoFragment>;
};

const getFollowersInfo = (
  data: Maybe<ArticleAuthorFollowersWithinContactsFragment>
): FollowersInfoType => {
  return {
    total: data?.total ?? 0,
    followers: data?.edges || [],
    pageInfo: data?.pageInfo || null,
  };
};

type ArticleAuthorFollowersProps = {
  articleId: string;
  author: Maybe<ArticleAuthorType>;
  isShown: boolean;
  onClose: () => void;
};

const ArticleAuthorFollowers: React.FC<ArticleAuthorFollowersProps> = ({
  articleId,
  author,
  isShown,
  onClose,
}) => {
  const intl = useIntl();

  const [totalFollowers, setTotalFollowers] = useState<number>(0);
  const [followersList, setFollowersList] = useState<
    Array<Maybe<ArticleAuthorFollowerFragment>>
  >([]);
  const [pageInfo, setPageInfo] =
    useState<Maybe<ArticleAuthorFollowersPageInfoFragment>>(null);

  const { loading, error, fetchMore } = useQuery<
    GetArticleAuthorFollowersByIdQuery,
    GetArticleAuthorFollowersByIdQueryVariables
  >(GetArticleAuthorFollowersByIdDocument, {
    errorPolicy: 'all',
    nextFetchPolicy: 'cache-and-network',
    variables: {
      articleID: articleId,
      first: FOLLOWER_CONTACTS__QUERY_LIMIT,
      after: null,
    },
    skip: !isShown,
    onCompleted: (data) => handleOnCompleted(data),
  });

  const handleOnCompleted = (
    data: GetArticleAuthorFollowersByIdQuery
  ): void => {
    const typename = data.articlesArticle?.context?.__typename;

    const followersQueryInfo =
      typename === 'ContentInsiderPage'
        ? data.articlesArticle?.context?.insiderPageMetadata
            .followersWithinContacts || null
        : typename === 'ContentPage'
          ? data.articlesArticle?.context?.entityPageMetadata
              ?.followersWithinContacts || null
          : null;

    const { total, followers, pageInfo } = getFollowersInfo(followersQueryInfo);

    setTotalFollowers(total);
    setFollowersList(followers);
    setPageInfo(pageInfo);
  };

  const isLoading = loading && !error;
  const hasError = !loading && error;

  const followersWithinContactsTotal =
    author?.metadata.followersWithinContacts.total ?? 0;

  const canFetchMore = followersList.length < totalFollowers && fetchMore;

  return (
    <PopUpWindow
      isShown={isShown}
      hide={onClose}
      data-qa="article-viewer-author-infobox-modal"
    >
      {isLoading ? (
        <ArticleAuthorFollowersSkeleton />
      ) : hasError ? (
        <>Something went wrong...</>
      ) : (
        <Styled.ArticleAuthorFollowersContainer>
          <Styled.ArticleAuthorFollowersHeadline size="xlarge">
            {intl.formatMessage(
              {
                id: getFollowersContactsKey(
                  followersWithinContactsTotal,
                  author?.gender || Gender.Other
                ),
              },
              {
                count: followersWithinContactsTotal,
              }
            )}
          </Styled.ArticleAuthorFollowersHeadline>

          <Styled.ArticleAuthorFollowersListContainer>
            {followersList.map((follower, index) => (
              <ArticleAuthorFollowerEntry key={index} follower={follower} />
            ))}
            {!loading && canFetchMore ? (
              <LazyLoading
                onLazyLoad={async () => {
                  await fetchMore({
                    variables: {
                      after: pageInfo?.endCursor,
                    },
                    updateQuery: (prevResult, { fetchMoreResult }) => {
                      const prevTypename =
                        prevResult.articlesArticle?.context?.__typename;
                      const typename =
                        fetchMoreResult.articlesArticle?.context?.__typename;

                      const newContext =
                        typename === 'ContentInsiderPage' &&
                        prevTypename === 'ContentInsiderPage'
                          ? {
                              ...fetchMoreResult.articlesArticle?.context,
                              insiderPageMetadata: {
                                followersWithinContacts: {
                                  ...fetchMoreResult.articlesArticle?.context
                                    ?.insiderPageMetadata
                                    ?.followersWithinContacts,
                                  total:
                                    fetchMoreResult.articlesArticle?.context
                                      ?.insiderPageMetadata
                                      ?.followersWithinContacts?.total ?? 0,
                                  edges: [
                                    ...(prevResult.articlesArticle?.context
                                      ?.insiderPageMetadata
                                      ?.followersWithinContacts?.edges || []),
                                    ...(fetchMoreResult.articlesArticle?.context
                                      ?.insiderPageMetadata
                                      ?.followersWithinContacts?.edges || []),
                                  ],
                                  pageInfo: {
                                    ...fetchMoreResult.articlesArticle?.context
                                      ?.insiderPageMetadata
                                      ?.followersWithinContacts?.pageInfo,
                                    hasNextPage:
                                      fetchMoreResult.articlesArticle?.context
                                        ?.insiderPageMetadata
                                        ?.followersWithinContacts?.pageInfo
                                        .hasNextPage ?? false,
                                    endCursor:
                                      fetchMoreResult.articlesArticle?.context
                                        ?.insiderPageMetadata
                                        ?.followersWithinContacts?.pageInfo
                                        .endCursor,
                                  },
                                },
                              },
                            }
                          : typename === 'ContentPage' &&
                              prevTypename === 'ContentPage'
                            ? {
                                ...fetchMoreResult.articlesArticle?.context,
                                entityPageMetadata: {
                                  ...fetchMoreResult.articlesArticle?.context
                                    ?.entityPageMetadata,
                                  followersWithinContacts: {
                                    ...fetchMoreResult.articlesArticle?.context
                                      ?.entityPageMetadata
                                      ?.followersWithinContacts,
                                    total:
                                      fetchMoreResult.articlesArticle?.context
                                        ?.entityPageMetadata
                                        ?.followersWithinContacts?.total ?? 0,
                                    edges: [
                                      ...(prevResult.articlesArticle?.context
                                        ?.entityPageMetadata
                                        ?.followersWithinContacts?.edges || []),
                                      ...(fetchMoreResult.articlesArticle
                                        ?.context?.entityPageMetadata
                                        ?.followersWithinContacts?.edges || []),
                                    ],
                                    pageInfo: {
                                      ...fetchMoreResult.articlesArticle
                                        ?.context?.entityPageMetadata
                                        ?.followersWithinContacts?.pageInfo,
                                      hasNextPage:
                                        fetchMoreResult.articlesArticle?.context
                                          ?.entityPageMetadata
                                          ?.followersWithinContacts?.pageInfo
                                          .hasNextPage ?? false,
                                      endCursor:
                                        fetchMoreResult.articlesArticle?.context
                                          ?.entityPageMetadata
                                          ?.followersWithinContacts?.pageInfo
                                          .endCursor,
                                    },
                                  },
                                },
                              }
                            : { ...prevResult.articlesArticle?.context };

                      const result = fetchMoreResult?.articlesArticle
                        ? {
                            articlesArticle: {
                              ...fetchMoreResult.articlesArticle,
                              context: {
                                ...newContext,
                              },
                            },
                          }
                        : prevResult;

                      handleOnCompleted(result);
                      return result;
                    },
                  });
                }}
              />
            ) : null}
          </Styled.ArticleAuthorFollowersListContainer>
        </Styled.ArticleAuthorFollowersContainer>
      )}
    </PopUpWindow>
  );
};

export default ArticleAuthorFollowers;
